import type { EdgeDirection, NodeMode } from '@hyphen-db-studio/architecture-core';
import { Boxes, Component, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { catalogByCategory, getCatalogEntry, NODE_CATEGORIES, NODE_CATEGORY_LABELS } from '../../lib/architectureNodeCatalog';
import ArchitectureJsonEditor from '../editor/ArchitectureJsonEditor';
import DiagramExplanation from '../workspace/DiagramExplanation';
import type { ReferenceHandler } from '../workspace/Markdown';
import { parseTourSteps } from '../../lib/tour';
import { useDiagramViewStore } from '../../store/useDiagramViewStore';
import { useArchitectureStore, type ArchitectureSelectionRef } from '../../store/useArchitectureStore';

const inputClass =
  'w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:border-sky-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100';
const dangerButtonClass =
  'flex w-full items-center justify-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10';

const DIRECTIONS: { value: EdgeDirection; label: string }[] = [
  { value: 'forward', label: 'Forward (→)' },
  { value: 'backward', label: 'Backward (←)' },
  { value: 'bidirectional', label: 'Bidirectional (↔)' },
  { value: 'none', label: 'None' },
];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="mb-3 block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">{label}</span>
      {children}
    </label>
  );
}

function NodeProperties({ nodeId }: { nodeId: string }) {
  const node = useArchitectureStore((s) => s.diagram.nodes.find((n) => n.id === nodeId));
  const allNodes = useArchitectureStore((s) => s.diagram.nodes);
  const updateNode = useArchitectureStore((s) => s.updateNode);
  const deleteNode = useArchitectureStore((s) => s.deleteNode);
  const setNodeMode = useArchitectureStore((s) => s.setNodeMode);
  const setNodeParent = useArchitectureStore((s) => s.setNodeParent);

  if (!node) return null;
  const entry = getCatalogEntry(node.kind);
  const mode: NodeMode = node.mode ?? 'component';
  const memberCount = allNodes.filter((n) => n.parentId === node.id).length;
  const groupCandidates = allNodes.filter((n) => n.mode === 'group' && n.id !== node.id);

  return (
    <div>
      <Field label="Mode">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setNodeMode(node.id, 'component')}
            className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-sm ${
              mode === 'component'
                ? 'border-sky-500 bg-sky-50 text-sky-700 dark:border-sky-400 dark:bg-sky-500/10 dark:text-sky-300'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'
            }`}
          >
            <Component className="h-3.5 w-3.5" /> Component
          </button>
          <button
            onClick={() => setNodeMode(node.id, 'group')}
            className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-sm ${
              mode === 'group'
                ? 'border-violet-500 bg-violet-50 text-violet-700 dark:border-violet-400 dark:bg-violet-500/10 dark:text-violet-300'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'
            }`}
          >
            <Boxes className="h-3.5 w-3.5" /> Group
          </button>
        </div>
        {mode === 'group' && (
          <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
            Wraps {memberCount} node{memberCount === 1 ? '' : 's'}. Drag a node onto it on the canvas, or set its parent below.
          </p>
        )}
      </Field>
      <Field label="Label">
        <input className={inputClass} value={node.label} onChange={(e) => updateNode(node.id, { label: e.target.value })} />
      </Field>
      <Field label="Kind">
        <select
          className={inputClass}
          value={node.kind}
          onChange={(e) => {
            const next = getCatalogEntry(e.target.value);
            if (next) updateNode(node.id, { kind: next.kind, category: next.category });
          }}
        >
          {NODE_CATEGORIES.map((category) => (
            <optgroup key={category} label={NODE_CATEGORY_LABELS[category]}>
              {catalogByCategory(category).map((c) => (
                <option key={c.kind} value={c.kind}>
                  {c.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </Field>
      {mode === 'component' && (
        <Field label="Parent group">
          <select
            className={inputClass}
            value={node.parentId ?? ''}
            onChange={(e) => setNodeParent(node.id, e.target.value || null)}
          >
            <option value="">No parent</option>
            {groupCandidates.map((g) => (
              <option key={g.id} value={g.id}>
                {g.label}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field label="Color override">
        <input
          type="color"
          className="h-8 w-full rounded-lg border border-gray-200 dark:border-gray-700"
          value={node.color ?? entry?.color ?? '#94a3b8'}
          onChange={(e) => updateNode(node.id, { color: e.target.value })}
        />
      </Field>
      <Field label="Note">
        <textarea
          className={inputClass}
          rows={3}
          value={node.note ?? ''}
          onChange={(e) => updateNode(node.id, { note: e.target.value })}
        />
      </Field>
      <button onClick={() => deleteNode(node.id)} className={dangerButtonClass}>
        <Trash2 className="h-3.5 w-3.5" /> Delete node
      </button>
    </div>
  );
}

function EdgeProperties({ edgeId }: { edgeId: string }) {
  const edge = useArchitectureStore((s) => s.diagram.edges.find((e) => e.id === edgeId));
  const updateEdge = useArchitectureStore((s) => s.updateEdge);
  const deleteEdge = useArchitectureStore((s) => s.deleteEdge);

  if (!edge) return null;

  return (
    <div>
      <Field label="Label">
        <input
          className={inputClass}
          value={edge.label ?? ''}
          placeholder="e.g. writes, reads, publishes"
          onChange={(e) => updateEdge(edge.id, { label: e.target.value })}
        />
      </Field>
      <Field label="Direction">
        <select
          className={inputClass}
          value={edge.direction}
          onChange={(e) => updateEdge(edge.id, { direction: e.target.value as EdgeDirection })}
        >
          {DIRECTIONS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
      </Field>
      <label className="mb-3 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
        <input type="checkbox" checked={edge.animated} onChange={(e) => updateEdge(edge.id, { animated: e.target.checked })} />
        Animate data flow
      </label>
      {edge.animated && (
        <Field label={`Speed (${(edge.speed ?? 1).toFixed(1)}x)`}>
          <input
            type="range"
            min={0.25}
            max={3}
            step={0.25}
            value={edge.speed ?? 1}
            onChange={(e) => updateEdge(edge.id, { speed: Number(e.target.value) })}
            className="w-full"
          />
        </Field>
      )}
      <button onClick={() => deleteEdge(edge.id)} className={dangerButtonClass}>
        <Trash2 className="h-3.5 w-3.5" /> Delete edge
      </button>
    </div>
  );
}

function ArchitectureDocumentation() {
  const documentation = useArchitectureStore((s) => s.documentation);
  const setDocumentation = useArchitectureStore((s) => s.setDocumentation);
  const nodeCount = useArchitectureStore((s) => s.diagram.nodes.length);
  const edgeCount = useArchitectureStore((s) => s.diagram.edges.length);
  const projectName = useArchitectureStore((s) => s.projectName);
  const storySteps = useMemo(() => parseTourSteps(documentation).length, [documentation]);
  const setSelection = useArchitectureStore((s) => s.setSelection);

  // Clicking a node reference in the explanation selects that node on the canvas (which centres on it) without leaving
  // the About tab. Exact label first, then a label that starts with the text, then one that contains it.
  const handleReference: ReferenceHandler = (kind, target) => {
    const view = useDiagramViewStore.getState();
    if (kind !== 'node') return view.showToast(`Unknown reference "${kind}:${target}"`);
    const wanted = target.toLowerCase();
    const nodes = useArchitectureStore.getState().diagram.nodes;
    const node =
      nodes.find((n) => n.label.toLowerCase() === wanted) ??
      nodes.find((n) => n.label.toLowerCase().startsWith(wanted)) ??
      nodes.find((n) => n.label.toLowerCase().includes(wanted));
    if (!node) return view.showToast(`"${target}" is not in this diagram any more`);
    useDiagramViewStore.setState({ keepAboutOpen: true });
    setSelection({ type: 'node', id: node.id });
  };

  return (
    <div>
      <div className="mb-4 grid grid-cols-2 gap-2 text-sm">
        <div className="rounded-md bg-gray-50 p-2 dark:bg-gray-800">
          <div className="text-xs text-gray-400 dark:text-gray-500">Nodes</div>
          <div className="font-semibold dark:text-gray-100">{nodeCount}</div>
        </div>
        <div className="rounded-md bg-gray-50 p-2 dark:bg-gray-800">
          <div className="text-xs text-gray-400 dark:text-gray-500">Edges</div>
          <div className="font-semibold dark:text-gray-100">{edgeCount}</div>
        </div>
      </div>
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">About this diagram</div>
      <DiagramExplanation
        value={documentation}
        onChange={setDocumentation}
        title={projectName}
        onReference={handleReference}
        storySteps={storySteps}
        onPlayStory={(start) => useDiagramViewStore.getState().openTour(start)}
        placeholder={'# What this diagram shows\n\nExplain how the parts fit together, key decisions and anything a newcomer should know...'}
      />
    </div>
  );
}

type PanelTab = 'properties' | 'documentation' | 'source';

function PropertiesTabContent({ selection }: { selection: ArchitectureSelectionRef }) {
  if (!selection) {
    return (
      <div className="p-3 text-sm text-gray-400 dark:text-gray-500">
        Nothing selected. Click a node or edge to edit its properties.
      </div>
    );
  }
  return (
    <div className="p-3">
      {selection.type === 'node' && <NodeProperties nodeId={selection.id} />}
      {selection.type === 'edge' && <EdgeProperties edgeId={selection.id} />}
    </div>
  );
}

export default function ArchitecturePropertiesPanel({ width }: { width: number }) {
  const selection = useArchitectureStore((s) => s.selection);
  const [activeTab, setActiveTab] = useState<PanelTab>('documentation');
  const docsRequested = useDiagramViewStore((s) => s.docsRequested);
  const requestDocs = useDiagramViewStore((s) => s.requestDocs);

  // The toolbar's "About" button asks for this tab (also when the panel was closed and has just been opened).
  useEffect(() => {
    if (!docsRequested) return;
    setActiveTab('documentation');
    requestDocs(false);
  }, [docsRequested, requestDocs]);

  useEffect(() => {
    if (!selection) return;
    // A reference clicked in the About tab selects something on the canvas; stay on About so the reader keeps their place.
    if (useDiagramViewStore.getState().keepAboutOpen) {
      useDiagramViewStore.setState({ keepAboutOpen: false });
      return;
    }
    setActiveTab('properties');
  }, [selection]);

  const tabs: { key: PanelTab; label: string }[] = [
    { key: 'documentation', label: 'About' },
    { key: 'properties', label: selection ? (selection.type === 'node' ? 'Node' : 'Edge') : 'Properties' },
    { key: 'source', label: 'JSON Source' },
  ];

  return (
    <div
      style={{ width }}
      className="flex h-full shrink-0 flex-col border-l border-gray-200/70 bg-panel dark:border-white/10 dark:bg-gray-900"
    >
      <div className="flex border-b border-gray-200/70 dark:border-white/10">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex-1 truncate px-2 py-2 text-sm font-medium ${
              activeTab === t.key
                ? 'border-b-2 border-sky-600 text-sky-700 dark:border-sky-400 dark:text-sky-300'
                : 'text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'properties' && <PropertiesTabContent selection={selection} />}
        {activeTab === 'documentation' && (
          <div className="p-3">
            <ArchitectureDocumentation />
          </div>
        )}
        {activeTab === 'source' && (
          <div className="h-full">
            <ArchitectureJsonEditor />
          </div>
        )}
      </div>
    </div>
  );
}
